import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notesApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import {
  Mic, MicOff, Send, Smile, Meh, Frown,
  PenSquare, Volume2, Loader2, Video, VideoOff, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';

export default function JournalPage() {
  const { t } = useLanguage();
  const [content, setContent] = useState('');
  const [mood, setMood] = useState([5]);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [videoRecording, setVideoRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('text');
  const [recentNote, setRecentNote] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);
  const videoStreamRef = useRef(null);
  const fileInputRef = useRef(null);

  const getMoodLabel = (value) => {
    if (value <= 2) return { label: t('journal_mood_verylow'), color: 'text-red-400' };
    if (value <= 4) return { label: t('journal_mood_low'), color: 'text-orange-400' };
    if (value <= 6) return { label: t('journal_mood_neutral'), color: 'text-yellow-400' };
    if (value <= 8) return { label: t('journal_mood_good'), color: 'text-lime-400' };
    return { label: t('journal_mood_great'), color: 'text-green-400' };
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        setAudioBlob(new Blob(chunksRef.current, { type: 'audio/webm' }));
        stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorder.start();
      setRecording(true);
    } catch { toast.error('Could not access microphone'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      videoStreamRef.current = stream;
      setVideoRecording(true);
      // Use requestAnimationFrame to ensure the video element is visible before assigning stream
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        setVideoBlob(new Blob(chunksRef.current, { type: 'video/webm' }));
        stream.getTracks().forEach(track => track.stop());
        if (videoRef.current) videoRef.current.srcObject = null;
      };
      mediaRecorder.start();
    } catch { toast.error('Could not access camera'); }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && videoRecording) {
      mediaRecorderRef.current.stop();
      setVideoRecording(false);
    }
  };

  const handleVideoFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100MB'); return; }
      setVideoBlob(file);
    }
  };

  const handleTextSubmit = async () => {
    if (!content.trim()) { toast.error('Please write something first'); return; }
    setLoading(true);
    try {
      const response = await notesApi.create({ content: content.trim(), mood: mood[0], type: 'text' });
      setRecentNote(response.data); setContent(''); setMood([5]);
      toast.success(t('journal_saved'));
    } catch { toast.error('Failed to save note'); }
    finally { setLoading(false); }
  };

  const handleAudioSubmit = async () => {
    if (!audioBlob) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('mood', mood[0]);
      const response = await notesApi.createAudio(formData);
      setRecentNote(response.data); setAudioBlob(null); setMood([5]);
      toast.success(t('journal_saved'));
    } catch { toast.error('Failed to process voice note'); }
    finally { setLoading(false); }
  };

  const handleVideoSubmit = async () => {
    if (!videoBlob) return;
    setLoading(true);
    try {
      const formData = new FormData();
      const fileName = videoBlob instanceof File ? videoBlob.name : 'recording.webm';
      formData.append('video', videoBlob, fileName);
      formData.append('mood', mood[0]);
      const response = await notesApi.createVideo(formData);
      setRecentNote(response.data); setVideoBlob(null); setMood([5]);
      toast.success(t('journal_saved'));
    } catch { toast.error('Failed to process video note'); }
    finally { setLoading(false); }
  };

  const moodInfo = getMoodLabel(mood[0]);

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="journal-page">
      <div>
        <h1 className="font-heading text-3xl text-white mb-2">{t('journal_title')}</h1>
        <p className="text-slate-400">{t('journal_subtitle')}</p>
      </div>

      <Card className="bg-navy-900/40 border-white/5">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-300">{t('journal_feeling')}</span>
            <span className={`font-medium ${moodInfo.color}`}>{mood[0]}/10 - {moodInfo.label}</span>
          </div>
          <Slider value={mood} onValueChange={setMood} max={10} min={1} step={1} className="w-full" data-testid="mood-slider" />
          <div className="flex justify-between mt-2">
            <Frown className="text-red-400" size={20} />
            <Meh className="text-yellow-400" size={20} />
            <Smile className="text-green-400" size={20} />
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-navy-900/60">
          <TabsTrigger value="text" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan" data-testid="text-tab">
            <PenSquare size={18} className="mr-2" />{t('journal_tab_text')}
          </TabsTrigger>
          <TabsTrigger value="voice" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold" data-testid="voice-tab">
            <Mic size={18} className="mr-2" />{t('journal_tab_voice')}
          </TabsTrigger>
          <TabsTrigger value="video" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400" data-testid="video-tab">
            <Video size={18} className="mr-2" />{t('journal_tab_video')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="mt-4">
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-6">
              <Textarea placeholder={t('journal_placeholder')} value={content} onChange={(e) => setContent(e.target.value)}
                className="min-h-[200px] bg-navy-950 border-white/10 text-white placeholder:text-slate-500 resize-none" data-testid="journal-textarea" />
              <div className="flex justify-between items-center mt-4">
                <span className="text-slate-500 text-sm">{content.length} {t('journal_characters')}</span>
                <Button onClick={handleTextSubmit} disabled={loading || !content.trim()} className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-6" data-testid="submit-text-btn">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <><Send size={18} className="mr-2" />{t('journal_save')}</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="voice" className="mt-4">
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-8 flex flex-col items-center">
              <motion.button whileTap={{ scale: 0.95 }} onClick={recording ? stopRecording : startRecording}
                className={`w-32 h-32 rounded-full flex items-center justify-center transition-all ${recording ? 'bg-red-500 recording-pulse' : 'bg-cyan/20 hover:bg-cyan/30 border-2 border-cyan'}`} data-testid="record-btn">
                {recording ? <MicOff className="text-white" size={48} /> : <Mic className="text-cyan" size={48} />}
              </motion.button>
              <p className="text-slate-300 mt-6 text-center">
                {recording ? <span className="text-red-400">{t('journal_recording')}</span> : audioBlob ? <span className="text-green-400">{t('journal_ready')}</span> : t('journal_record')}
              </p>
              {audioBlob && !recording && <div className="mt-6 w-full"><audio src={URL.createObjectURL(audioBlob)} controls className="w-full" /></div>}
              {audioBlob && !recording && (
                <Button onClick={handleAudioSubmit} disabled={loading} className="mt-6 bg-gold text-navy-950 hover:bg-gold-muted rounded-full px-8" data-testid="submit-audio-btn">
                  {loading ? <><Loader2 className="animate-spin mr-2" size={18} />{t('journal_processing')}</> : <><Volume2 size={18} className="mr-2" />{t('journal_transcribe')}</>}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="video" className="mt-4">
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-8 flex flex-col items-center">
              {/* Video preview */}
              <div className="w-full max-w-md aspect-video bg-navy-950 rounded-xl overflow-hidden mb-6 relative">
                <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${videoRecording ? 'block' : 'hidden'}`} />
                {videoBlob && !videoRecording && (
                  <video src={URL.createObjectURL(videoBlob)} controls className="w-full h-full object-cover" />
                )}
                {!videoRecording && !videoBlob && (
                  <div className="w-full h-full flex items-center justify-center text-slate-500">
                    <Video size={48} className="opacity-30" />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <motion.button whileTap={{ scale: 0.95 }} onClick={videoRecording ? stopVideoRecording : startVideoRecording}
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${videoRecording ? 'bg-red-500 recording-pulse' : 'bg-purple-500/20 hover:bg-purple-500/30 border-2 border-purple-500'}`} data-testid="video-record-btn">
                  {videoRecording ? <VideoOff className="text-white" size={28} /> : <Video className="text-purple-400" size={28} />}
                </motion.button>

                {!videoRecording && (
                  <>
                    <span className="text-slate-500 text-sm">{t('journal_video_or')}</span>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="border-white/10 text-slate-300 hover:text-white" data-testid="video-upload-btn">
                      <Upload size={16} className="mr-2" />{t('journal_video_upload')}
                    </Button>
                    <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoFileUpload} />
                  </>
                )}
              </div>

              <p className="text-slate-300 mt-4 text-center text-sm">
                {videoRecording ? <span className="text-red-400">{t('journal_video_recording')}</span> : videoBlob ? <span className="text-green-400">{t('journal_video_ready')}</span> : t('journal_video_record')}
              </p>

              {videoBlob && !videoRecording && (
                <Button onClick={handleVideoSubmit} disabled={loading} className="mt-6 bg-purple-500 text-white hover:bg-purple-600 rounded-full px-8" data-testid="submit-video-btn">
                  {loading ? <><Loader2 className="animate-spin mr-2" size={18} />{t('journal_video_processing')}</> : <><Video size={18} className="mr-2" />{t('journal_transcribe')}</>}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AnimatePresence>
        {recentNote && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
            <Card className="bg-gradient-to-br from-cyan/10 to-gold/5 border-cyan/20">
              <CardContent className="p-6">
                <h3 className="text-cyan font-medium mb-2">{t('journal_saved')}</h3>
                <p className="text-slate-200">{recentNote.content || recentNote.transcript}</p>
                {recentNote.sentiment && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${recentNote.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' : recentNote.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' : 'bg-slate-500/20 text-slate-400'}`}>
                      {recentNote.sentiment}
                    </span>
                    {recentNote.tags?.map(tag => <span key={tag} className="text-xs px-2 py-1 rounded-full bg-navy-800 text-slate-300">{tag}</span>)}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
